/**
 * Variant Gallery — per-variant Metafield image groups
 * ------------------------------------------------------------------
 * On the product page, switches the media gallery (main image +
 * thumbnails) to the image set configured for the selected variant via
 * variant-level Metafields (namespace `custom`, keys `variant_gallery`,
 * `variant_gallery_2` … `variant_gallery_6`, type "File", multi-select).
 *
 * Data is injected by main-product.liquid:
 *   <script id="variant-gallery-data-{section}" type="application/json">
 *     {"variants":[{"id":<variant id>,"gallery":[<image URL strings>]}]}
 *
 * How it hooks into the theme:
 *   - The theme routes every option change through product-info, which
 *     updates the form's hidden input[name="id"] and then publishes the
 *     'variant-change' pub/sub event. We subscribe to that event (more
 *     reliable than raw change/input on the pickers — the form id is
 *     always current, and multi-option variants resolve correctly).
 *   - 'product-info:loaded' covers re-renders / product swaps.
 *   - We also run once on load for the initially selected variant.
 *
 * Behavior per variant:
 *   - Non-empty Metafield gallery  -> rebuild the main <ul> and the
 *     thumbnail <ul> from those images, activate the first image.
 *   - Empty gallery                -> keep Dawn's default product images
 *     (product-info.updateMedia already refreshed the main viewer) and
 *     restore the cached default thumbnails.
 */
(function () {
  if (window.VariantGallery) return;

  // Full snapshot (main <ul> + thumbnail <ul>) of the server-rendered default
  // gallery, captured once before any custom gallery is applied, so we can
  // restore it wholesale when a variant without a Metafield gallery is picked.
  var defaultGalleryCache = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
  var dataCache = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
  var imageWidths = [493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, 1946];

  function escapeAttr(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // Uniformly normalize an image URL before rendering, mirroring the
  // theme's native gallery (image_url): force https (metafield URLs are
  // usually protocol-relative like //cdn.shopify.com/...) and drop any
  // pre-existing width param so we control resolution via withWidth().
  function normalizeImageUrl(url) {
    if (!url) return '';
    var clean = String(url).trim();
    if (clean.indexOf('//') === 0) {
      clean = 'https:' + clean;
    } else if (clean.indexOf('http://') === 0) {
      clean = 'https:' + clean.slice('http:'.length);
    }
    clean = clean.replace(/([?&])width=\d+/gi, '$1').replace(/(\?|&)$/, '');
    return clean;
  }

  function withWidth(url, width) {
    var base = normalizeImageUrl(url);
    var sep = base.indexOf('?') === -1 ? '?' : '&';
    return base + sep + 'width=' + width;
  }

  function buildSrcSet(url) {
    var base = normalizeImageUrl(url);
    var parts = [];
    for (var i = 0; i < imageWidths.length; i++) {
      parts.push(withWidth(base, imageWidths[i]) + ' ' + imageWidths[i] + 'w');
    }
    parts.push(base);
    return parts.join(', ');
  }

  function flattenArray(value) {
    var out = [];
    if (!Array.isArray(value)) return out;
    value.forEach(function (item) {
      if (Array.isArray(item)) {
        out = out.concat(flattenArray(item));
      } else if (item !== null && item !== undefined) {
        out.push(item);
      }
    });
    return out;
  }

  function readData(root) {
    var script = root.querySelector('[id^="variant-gallery-data"]');
    if (!script) return null;
    if (dataCache && dataCache.has(script)) return dataCache.get(script);

    var map = {};
    try {
      var parsed = JSON.parse(script.textContent);
      (parsed.variants || []).forEach(function (variant) {
        if (variant && variant.id != null) {
          // flatten in case a gallery ever arrives nested (defensive)
          map[String(variant.id)] = flattenArray(variant.gallery).filter(function (url) {
            return !!url;
          });
        }
      });
    } catch (err) {
      return null;
    }

    // Media container settings from the section, so the custom slides size
    // exactly like the theme's native gallery (viewport constraint + fit).
    var result = {
      map: map,
      constrain: script.getAttribute('data-constrain-viewport') === 'true',
      mediaFit: script.getAttribute('data-media-fit') || 'contain'
    };
    if (dataCache) dataCache.set(script, result);
    return result;
  }

  function getSelectedVariantId(root) {
    // Primary: the product form's hidden id input. product-info updates it
    // before 'variant-change' is published, so it always holds the current
    // variant (handles multi-option variants reliably).
    var inputs = root.querySelectorAll('input[name="id"]');
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].value) return String(inputs[i].value);
    }
    // Fallback: the <variant-selects> data-selected-variant JSON blob.
    var selected = root.querySelector('variant-selects [data-selected-variant]');
    if (selected) {
      try {
        var variant = JSON.parse(selected.innerHTML);
        if (variant && variant.id) return String(variant.id);
      } catch (err) {}
    }
    return null;
  }

  function bindThumbnailClicks(galleryEl, thumbList) {
    if (!thumbList || typeof galleryEl.setActiveMedia !== 'function') return;
    thumbList.querySelectorAll('[data-target]').forEach(function (item) {
      var button = item.querySelector('button');
      if (!button) return;
      button.addEventListener('click', function () {
        galleryEl.setActiveMedia(item.dataset.target, false);
      });
    });
  }

  function buildSlide(mediaId, url, index, opts) {
    var constrainClass = opts && opts.constrain ? ' constrain-height' : '';
    var fitClass = opts && opts.mediaFit ? ' media-fit-' + opts.mediaFit : '';
    return (
      '<li id="Slide-' + mediaId + '" class="protopimg-item product__media-item grid__item slider__slide" data-media-id="' + mediaId + '">' +
        '<div class="product-media-container media-type-image' + constrainClass + fitClass + ' gradient" style="--ratio: 1; --preview-ratio: 1;">' +
          '<div class="product__modal-opener--image">' +
            '<div class="product__media media media--transparent global-media-settings" style="padding-top: 100%;">' +
              '<img src="' + escapeAttr(withWidth(url, 1946)) + '"' +
                ' srcset="' + escapeAttr(buildSrcSet(url)) + '"' +
                ' sizes="(min-width: 990px) 55vw, (min-width: 768px) calc(100vw - 11.5rem), calc(100vw - 4rem)"' +
                ' alt="Product image ' + (index + 1) + '"' +
                ' loading="' + (index === 0 ? 'eager' : 'lazy') + '"' +
                ' fetchpriority="' + (index === 0 ? 'high' : 'auto') + '"' +
                ' width="0" height="0" data-width="0" data-height="0">' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</li>'
    );
  }

  function buildThumb(mediaId, url, index, viewerId) {
    return (
      '<li class="thumbnail-list__item slider__slide" data-target="' + mediaId + '" data-media-position="' + (index + 1) + '">' +
        '<button class="thumbnail global-media-settings global-media-settings--no-shadow"' +
          ' aria-label="Load image ' + (index + 1) + ' in gallery view"' +
          ' aria-controls="' + viewerId + '">' +
          '<img src="' + escapeAttr(withWidth(url, 416)) + '"' +
            ' alt="Product image ' + (index + 1) + '" loading="lazy">' +
        '</button>' +
      '</li>'
    );
  }

  // (Re)wire the PhotoSwipe lightbox for the custom slides inside this
  // product-info. initProductPhotoSwipe only picks up slides whose img has
  // valid data-width/data-height (or an already-loaded naturalWidth), so it
  // is called again once each image is ready.
  function refreshLightbox(root) {
    if (!root || typeof window.initProductPhotoSwipe !== 'function') return;
    window.initProductPhotoSwipe(root);
  }

  // Dawn sizes the slide container from the image's aspect ratio, and the
  // lightbox needs the image's dimensions. We don't know the Metafield file
  // dimensions up front, so once each image loads we set the container
  // ratio + data-width/data-height, then refresh the lightbox.
  function wireSlideImages(list, root) {
    if (!list) return;
    list.querySelectorAll('li[data-media-id] img').forEach(function (img) {
      var makeReady = function () {
        if (!img.naturalWidth || !img.naturalHeight) return;
        var container = img.closest('.product-media-container');
        if (container) {
          var ratio = img.naturalWidth / img.naturalHeight;
          // Let the theme CSS size the container (incl. viewport constraint)
          container.style.setProperty('--ratio', ratio);
          container.style.setProperty('--preview-ratio', ratio);
        }
        img.setAttribute('width', String(img.naturalWidth));
        img.setAttribute('height', String(img.naturalHeight));
        img.setAttribute('data-width', String(img.naturalWidth));
        img.setAttribute('data-height', String(img.naturalHeight));
        refreshLightbox(root);
      };
      if (img.complete) {
        makeReady();
      } else {
        img.addEventListener('load', makeReady);
      }
    });
  }

  function applyCustomGallery(galleryEl, images, root, opts) {
    var viewer = galleryEl.querySelector('[id^="GalleryViewer"]');
    var thumbSlider = galleryEl.querySelector('[id^="GalleryThumbnails"]');
    var list = viewer && viewer.querySelector('[id^="Slider-Gallery"]');
    var thumbList = thumbSlider && thumbSlider.querySelector('[id^="Slider-Thumbnails"]');
    if (!viewer || !list || !images.length) return;

    var uid = (galleryEl.id || 'gallery').replace(/^MediaGallery-/, '');
    var viewerId = viewer.id || '';

    // Snapshot the server-rendered default gallery once (before we overwrite
    // it), so it can be restored wholesale for variants without a Metafield
    // gallery — instead of relying on the theme's incremental media diff.
    if (defaultGalleryCache && !defaultGalleryCache.has(galleryEl)) {
      defaultGalleryCache.set(galleryEl, {
        main: list.innerHTML,
        thumbs: thumbList ? thumbList.innerHTML : null
      });
    }

    var slidesHtml = '';
    var thumbsHtml = '';
    for (var i = 0; i < images.length; i++) {
      var mediaId = 'vg-' + uid + '-' + i;
      slidesHtml += buildSlide(mediaId, images[i], i, opts);
      if (thumbList) thumbsHtml += buildThumb(mediaId, images[i], i, viewerId);
    }

    list.innerHTML = slidesHtml;
    if (thumbList) thumbList.innerHTML = thumbsHtml;
    bindThumbnailClicks(galleryEl, thumbList);
    wireSlideImages(list, root);

    if (typeof viewer.resetPages === 'function') viewer.resetPages();
    if (thumbSlider && typeof thumbSlider.resetPages === 'function') thumbSlider.resetPages();

    galleryEl.dataset.vgCustom = 'true';

    if (typeof galleryEl.setActiveMedia === 'function') {
      galleryEl.setActiveMedia('vg-' + uid + '-0', true);
    }
    refreshLightbox(root);

    if (images.length <= 1) {
      var currentEl = viewer.querySelector('.slider-counter--current');
      var totalEl = viewer.querySelector('.slider-counter--total');
      if (currentEl) currentEl.textContent = '1';
      if (totalEl) totalEl.textContent = '1';
      if (viewer.prevButton) viewer.prevButton.setAttribute('disabled', 'disabled');
      if (viewer.nextButton) viewer.nextButton.setAttribute('disabled', 'disabled');
    }
  }

  // Authoritative featured media id of the currently selected variant,
  // read from the <variant-selects> JSON blob (re-rendered on variant change).
  function getFeaturedMediaId(root) {
    if (!root) return null;
    var selected = root.querySelector('variant-selects [data-selected-variant]');
    if (!selected) return null;
    try {
      var variant = JSON.parse(selected.innerHTML);
      if (variant && variant.featured_media && variant.featured_media.id) {
        return String(variant.featured_media.id);
      }
    } catch (err) {}
    return null;
  }

  function restoreDefaultGallery(galleryEl, root) {
    if (galleryEl.dataset.vgCustom !== 'true') return;

    delete galleryEl.dataset.vgCustom;

    var viewer = galleryEl.querySelector('[id^="GalleryViewer"]');
    var mainList = viewer && viewer.querySelector('[id^="Slider-Gallery"]');
    var thumbSlider = galleryEl.querySelector('[id^="GalleryThumbnails"]');
    var thumbList = thumbSlider && thumbSlider.querySelector('[id^="Slider-Thumbnails"]');

    var cached = defaultGalleryCache ? defaultGalleryCache.get(galleryEl) : null;
    if (!cached || !mainList) return;

    // Restore the full default gallery (main viewer + thumbnails) so it never
    // carries over custom slides or stale thumbnails.
    if (cached.main != null) mainList.innerHTML = cached.main;
    if (thumbList && cached.thumbs != null) thumbList.innerHTML = cached.thumbs;
    bindThumbnailClicks(galleryEl, thumbList);

    if (typeof viewer.resetPages === 'function') viewer.resetPages();
    if (thumbSlider && typeof thumbSlider.resetPages === 'function') thumbSlider.resetPages();

    // Activate this variant's featured media slide (it exists in the default
    // gallery since that contains every product image), falling back to the
    // first slide when the variant has no featured media.
    var targetId = null;
    var featuredId = getFeaturedMediaId(root);
    if (featuredId) {
      var suffix = '-' + featuredId;
      var slides = mainList.querySelectorAll('li[data-media-id]');
      for (var i = 0; i < slides.length; i++) {
        var slideId = slides[i].dataset.mediaId;
        if (slideId === featuredId || slideId.slice(-suffix.length) === suffix) {
          targetId = slideId;
          break;
        }
      }
    }
    if (!targetId) {
      var firstSlide = mainList.querySelector('li[data-media-id]');
      if (firstSlide) targetId = firstSlide.dataset.mediaId;
    }
    if (targetId && typeof galleryEl.setActiveMedia === 'function') {
      galleryEl.setActiveMedia(targetId, true);
    }
  }

  function applyGallery(root) {
    if (!root || root.tagName !== 'PRODUCT-INFO') return;
    var data = readData(root);
    if (!data) return;

    var variantId = getSelectedVariantId(root);
    if (!variantId) return;

    var images = data.map[variantId] || [];
    var opts = { constrain: !!data.constrain, mediaFit: data.mediaFit || 'contain' };
    var galleries = root.querySelectorAll('media-gallery');
    for (var i = 0; i < galleries.length; i++) {
      if (images.length) {
        applyCustomGallery(galleries[i], images, root, opts);
      } else {
        restoreDefaultGallery(galleries[i], root);
      }
    }
  }

  function applyAll() {
    var roots = document.querySelectorAll('product-info');
    for (var i = 0; i < roots.length; i++) applyGallery(roots[i]);
  }

  // Initial load. (The 'product-info:loaded' event for the initially
  // rendered markup already fired before this deferred script runs.)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyAll);
  } else {
    applyAll();
  }

  // Same-product variant change: fires after product-info updated the
  // form's hidden id input and refreshed the default media gallery.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, applyAll);
  }

  // product-info re-created (product swap / full re-render).
  document.addEventListener('product-info:loaded', applyAll);

  window.VariantGallery = { applyAll: applyAll };
})();
